import { useLinkageDocuments } from './LinkageDocuments'
import { useContext, useEffect, useRef, useState } from 'react'
import { sileo } from 'sileo'
import GlobalContext from '../../../../../../../context/global-context'
import CustomButton from '../../../../../ui/CustomButton'
import { Title } from '../../../../../ui'
import ResizableModal from '../../../../../ui/ResizableModal'
import { useLinkageAmount, useLinkageBusinessAPI } from '../LinkageCreationContext'
import { OrganizationAPI } from '../../../../../../../api/organization.api'
import Lottie from 'lottie-react'
import { Box } from '@mui/material'
import LoaderMain from '../../../../../../../assets/svgIcons/loaderMain.json'

const organizationAPI = new OrganizationAPI()

const InputField = ({ label, ...props }) => (
  <label className='block text-sm font-sans font-medium text-gray-700'>
    {label}
    <input {...props} className='mt-1 block w-full rounded-full border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans disabled:bg-gray-100 disabled:text-gray-500' />
  </label>
)

const formatRut = input => {
  const cleaned = input.replace(/[^0-9kK]/g, '').toUpperCase().slice(0, 9)
  const body = cleaned.slice(0, -1)
  const dv = cleaned.slice(-1)
  return body ? `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv}` : dv
}

const InvoiceImportaciones = ({ open, setOpen, setSwitchDate, folio, idLibro, idOrigin, type = 'egreso', action = 'crear' }) => {
  const isEditing = action === 'modificar'
  const { currentBusiness } = useContext(GlobalContext)
  const businessAPI = useLinkageBusinessAPI()
  const [modoCarga, setModoCarga] = useState('masiva')
  const attachment = useLinkageDocuments({ open, action, idLibro, idOrigin, businessId: currentBusiness?.id, businessAPI, enabled: isEditing || modoCarga === 'individual' })
  const [fecha, setFecha] = useState(() => new Date().toISOString().split('T')[0])
  const [vencimiento, setVencimiento] = useState(() => new Date().toISOString().split('T')[0])
  const [razonSocial, setRazonSocial] = useState('')
  const [rut, setRut] = useState('')
  const [din, setDin] = useState('')
  const [glosa, setGlosa] = useState('')
  const [monto, setMonto, bankAmountLocked] = useLinkageAmount('')
  const [archivoAdjunto, setArchivoAdjunto] = useState(null)
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [loadedKey, setLoadedKey] = useState(null)
  const [loadError, setLoadError] = useState(false)
  const [nombreComprobante, setNombreComprobante] = useState('Invoice')
  const recordKey = `${currentBusiness?.id}:${idLibro}:${type}`
  const editPending = isEditing && loadedKey !== recordKey
  const savingRef = useRef(false)
  const fileInputRef = useRef(null)
  const handleClose = () => { if (!savingRef.current) setOpen(false) }

  useEffect(() => {
    if (!open || !isEditing) return
    let cancelled = false
    setLoadedKey(null)
    setLoadError(false)
    const load = async () => {
      try {
        if (!currentBusiness?.id || !idLibro) throw new Error('Registro no disponible')
        const { data } = await organizationAPI.getDataLibroContable(currentBusiness.id, idLibro, type.trim())
        if (cancelled) return
        setFecha(String(data.fecha || '').slice(0, 10))
        setVencimiento(String(data.fechaVencimiento || data.vencimiento || '').slice(0, 10))
        setRazonSocial(data.razonSocial || '')
        setRut(data.rut || '')
        setMonto(String(data.montoTotal ?? data.monto ?? ''))
        setGlosa(data.glosa || '')
        setDin(String(data.folio ?? data.numeroFolio ?? folio ?? ''))
        setNombreComprobante(data.nombreCodigoDocumento || data.nombreComprobante || 'Invoice')
        setLoadedKey(recordKey)
      } catch (error) {
        if (!cancelled) setLoadError(true)
      }
    }
    load()
    return () => { cancelled = true }
  }, [open, isEditing, currentBusiness?.id, idLibro, type, recordKey, setMonto, folio])

  const handleDownloadTemplate = async () => {
    if (isDownloadingTemplate) return
    setIsDownloadingTemplate(true)
    try {
      const response = await businessAPI.downloadRemunerationTemplate('plantillas-invoice')
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      try {
        link.href = url
        link.download = 'InvoiceImportaciones.xlsx'
        document.body.appendChild(link)
        link.click()
      } finally {
        link.remove()
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      sileo.error({
        title: 'Error de Descarga',
        description: 'No se pudo descargar la plantilla. Intenta nuevamente.'
      })
    } finally {
      setIsDownloadingTemplate(false)
    }
  }

  const handleSave = async () => {
    if (savingRef.current || editPending || action === 'replicar') return
    let payload
    if (!isEditing && modoCarga === 'masiva') {
      if (!archivoAdjunto || !/\.(xlsx|xls)$/i.test(archivoAdjunto.name)) {
        sileo.error({ title: 'Archivo requerido', description: 'Selecciona un archivo Excel (.xlsx o .xls).' })
        return
      }
      payload = new FormData()
      payload.append('file', archivoAdjunto)
    } else {
      if (!fecha || !vencimiento || !razonSocial.trim() || !rut || !glosa.trim() || !Number.isFinite(Number(monto)) || Number(monto) <= 0) {
        sileo.error({ title: 'Campos obligatorios', description: 'Completa todos los campos excepto DIN Asociado e ingresa un monto mayor a cero.' })
        return
      }
      payload = {
        fecha,
        vencimiento,
        transaccionId: null,
        razonSocial,
        rut,
        rutId: null,
        nombreComprobante,
        nombreComprobanteId: null,
        glosa,
        respaldoId: null,
        monto: Number(monto),
        folio: din.trim()
      }
    }
    savingRef.current = true
    setIsSaving(true)
    try {
      if (isEditing) {
        const { fecha, vencimiento, razonSocial, rut, nombreComprobante, glosa, monto, folio } = payload
        await organizationAPI.updateVinculacion(currentBusiness.id, idLibro, {
          fecha, vencimiento, razonSocial, rut, nombreComprobante, glosa, monto, folio
        })
      } else if (modoCarga === 'masiva') {
        await businessAPI.uploadModelLibroExcel(currentBusiness.id, payload, 'invoice-importacion')
      } else {
        const response = await businessAPI.postPagoArriendo(currentBusiness.id, payload, 'invoice-importacion')
        await attachment.upload(response?.data?.id)
      }
      setSwitchDate(true)
      setOpen(false)
    } catch (error) {
      sileo.error({ title: 'Error al guardar', description: 'No se pudo guardar Invoice Importaciones. Intenta nuevamente.' })
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
  }

  return (
    <ResizableModal open={open} onClose={handleClose} footer={
      <>
        <CustomButton title='CANCELAR' color='primary' className='rounded-full' disabled={isSaving} onClick={handleClose} />
        <CustomButton title={isSaving ? 'GUARDANDO...' : 'GRABAR VINCULACIÓN'} color='accent2' className='rounded-full' disabled={isSaving || attachment.busy || editPending || action === 'replicar'} onClick={handleSave} />
      </>
    }>
      {editPending && !loadError ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%' role='status' aria-label='Cargando vinculación'>
          <Lottie animationData={LoaderMain} loop autoplay style={{ width: 100, height: 100 }} />
        </Box>
      ) : (
        <Title className='text-2xl font-sans mb-4'>Invoice Importaciones</Title>
      )}
      {editPending && loadError && <p role='alert' className='mb-4 text-sm font-sans'>No se pudo cargar la vinculación. Cierra el modal y vuelve a intentarlo.</p>}
      {!editPending && <div className='bg-white p-3 rounded-lg shadow-md'>
        {!isEditing && (
        <div className='mb-6 ml-auto flex w-fit rounded-full border border-gray-200 bg-gray-100 p-1 shadow-sm'>
          {[['individual', 'Carga individual'], ['masiva', 'Carga masiva']].map(([mode, label]) => (
            <button key={mode} type='button' disabled={isSaving} aria-pressed={modoCarga === mode} onClick={() => setModoCarga(mode)}
              className={`rounded-full px-4 py-2 text-xs font-semibold uppercase transition-colors ${modoCarga === mode ? 'bg-accent2 text-white shadow-sm' : 'text-gray-500 hover:text-primary'}`}>
              {label}
            </button>
          ))}
        </div>
        )}
        {!isEditing && modoCarga === 'masiva' ? (
          <div>
          <h3 className='mb-6 text-xl font-semibold font-sans text-gray-900'>Carga masiva por Excel</h3>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.9fr]'>
            <div className='rounded-2xl border border-slate-200 bg-slate-50 p-5'>
              <p className='text-sm font-semibold font-sans text-gray-800'>Para ingresar datos de forma masiva, por favor sigue las siguientes instrucciones</p>
              <ol className='mt-3 list-decimal space-y-3 pl-5 text-sm font-sans text-gray-600'>
                <li><CustomButton type='button' title={isDownloadingTemplate ? 'DESCARGANDO...' : 'DESCARGAR PLANTILLA EXCEL'} color='accent2' className='rounded-full text-xs' disabled={isDownloadingTemplate} onClick={handleDownloadTemplate} /></li>
                <li>Ingresa los datos en la plantilla descargada.</li>
                <li>Guarda el archivo en formato Excel (.xlsx o .xls).</li>
                <li>Selecciona el archivo y presiona Grabar vinculación.</li>
              </ol>
              <p className='mt-3 text-xs font-sans text-gray-500'>El archivo Excel es obligatorio para la carga masiva.</p>
            </div>
            <div className='rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-6'>
              <div className='flex h-full flex-col items-center justify-center gap-4 text-center'>
                <div className='space-y-2'>
                  <p className='text-sm font-semibold font-sans text-gray-800'>Archivo Excel</p>
                  <p className='text-xs font-sans text-gray-500'>Selecciona la plantilla completada para procesar la carga masiva de Invoice Importaciones.</p>
                </div>
                <input ref={fileInputRef} type='file' accept='.xlsx,.xls' disabled={isSaving} aria-label='Archivo adjunto'
                  className='hidden' onChange={e => setArchivoAdjunto(e.target.files?.[0] || null)} />
              {archivoAdjunto ? (
                <div className='w-full rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-left'>
                  <p className='text-xs font-semibold font-sans text-primary'>Archivo seleccionado</p>
                  <p className='mt-1 break-all text-sm font-sans text-gray-700'>{archivoAdjunto.name}</p>
                  <button type='button' disabled={isSaving} className='mt-3 text-xs font-semibold font-sans text-red-500 hover:text-red-600' onClick={() => {
                    setArchivoAdjunto(null)
                    if (fileInputRef.current) fileInputRef.current.value = ''
                  }}>Quitar archivo</button>
                </div>
              ) : (
                <div className='w-full rounded-2xl bg-white/80 px-4 py-6'>
                  <p className='text-sm font-sans text-gray-500'>Aun no has seleccionado un archivo Excel para importar.</p>
                </div>
              )}
                <div className='flex w-full flex-col gap-3'>
                  <CustomButton type='button' title={archivoAdjunto ? 'CAMBIAR ARCHIVO' : 'SELECCIONAR ARCHIVO'}
                    color='primary' className='w-full rounded-full text-sm' disabled={isSaving} onClick={() => fileInputRef.current?.click()} />
                </div>
              </div>
            </div>
          </div>
          </div>
        ) : (
          <fieldset disabled={isSaving} className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full'>
            <InputField label='Fecha' type='date' value={fecha} onChange={e => setFecha(e.target.value)} />
            <InputField label='Fecha Vencimiento' type='date' value={vencimiento} onChange={e => setVencimiento(e.target.value)} />
            <InputField label='Proveedor Extranjero' placeholder='Nombre' type='text' value={razonSocial} onChange={e => setRazonSocial(e.target.value)} />
            <InputField label='RUT/TAX ID' placeholder='11.111.111-1' type='text' value={rut} onChange={e => setRut(formatRut(e.target.value))} />
            <InputField label='DIN Asociado' placeholder='Número DIN' type='text' value={din} onChange={e => setDin(e.target.value)} />
            <InputField label='Tipo Documento' type='text' value={nombreComprobante} disabled />
            <InputField label='Monto CLP' placeholder='$' type='text' disabled={bankAmountLocked} value={monto ? new Intl.NumberFormat('es-CL').format(Number(monto)) : ''} onChange={e => setMonto(e.target.value.replace(/[^0-9]/g, ''))} />
            <InputField label='Clasificación' placeholder='1-1-8-2 Mercaderías' type='text' disabled />
            <label className='block text-sm font-sans font-medium text-gray-700'>
              Detalle
              <textarea className='mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm font-sans' maxLength={50} value={glosa} onChange={e => setGlosa(e.target.value)}
                placeholder='Describe en detalle la naturaleza, origen o lo que mejor describa la vinculación que vamos a crear.' />
              <span className='text-xs text-gray-500 mt-1'>{glosa.length}/50 caracteres</span>
            </label>
            {attachment.panel}
          </fieldset>
        )}
      </div>}
    </ResizableModal>
  )
}

export default InvoiceImportaciones
